import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useState, useRef, useEffect } from "react";

import { useProfileData } from "../../../../hooks/useProfileData";

import GinoChat from "../../components/GinoChat";
import GinoHeader from "../../components/GinoHeader";
import { useGetGinoCredits } from "../../api/queries/ginoCredits";
import { useGetMessages } from "../../api/queries/useGetMessages";
import { useGetOrCreateDirectRoom } from "../../api/queries/getOrCreateDirectRoom";
import { useSendMessagesToGinoUpdated } from "../../hooks/useSendMessagesToGinoUpdated";
import { GinoMessage, GinoOpeningMessage, ThinkingMessage } from "../../components/Messages";

const GinoChatPage = () => {
  const { t } = useTranslation("gino", { keyPrefix: "chatPage" });
  const [searchParams] = useSearchParams();
  const about = searchParams.get("about") as
    | "player"
    | "professional"
    | "trainingPlan"
    | "nutritionPlan"
    | "trialPrepTryout"
    | null;

  const {
    data: { id: userId },
  } = useProfileData();
  const { data: directRoom, isLoading: isLoadingDirectRoom } = useGetOrCreateDirectRoom();
  const { data: messages, isLoading: isLoadingMessages } = useGetMessages(String(directRoom?.chatId));

  const {
    chatInputRef,
    handleSendTextMessage,
    handleOnKeyDownWithinChatInput,
    isLoading: isSendingGinoMessages,
    error,
  } = useSendMessagesToGinoUpdated({
    userId,
    chatId: directRoom?.chatId,
  });

  const [openCreditLeftModal, setOpenCreditLeftModal] = useState(false);
  const handleOpenCreditLeftModal = () => setOpenCreditLeftModal(true);
  const handleCloseCreditLeftModal = () => setOpenCreditLeftModal(false);

  const { data: ginoCredits, isLoading: isLoadingGinoCredits, error: errorGinoCredits } = useGetGinoCredits();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessagesLengthRef = useRef<number>(0);
  const lastSeenMessageIdRef = useRef<number | null>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesContainerRef.current?.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  useEffect(() => {
    if (messages && messages.length > 0 && userId) {
      const currentMessagesLength = messages.length;
      const lastMessage = messages[messages.length - 1];

      // Check if a new message was added
      const isNewMessage = currentMessagesLength > previousMessagesLengthRef.current;

      // Check if the new message is from the user (not GINO)
      const isLastMessageFromUser = lastMessage.sender_id === userId;

      // Only scroll if a new user message was added
      if (isNewMessage && isLastMessageFromUser && lastMessage.id !== lastSeenMessageIdRef.current) {
        scrollToBottom();
        lastSeenMessageIdRef.current = lastMessage.id;
      }

      // Update the previous length
      previousMessagesLengthRef.current = currentMessagesLength;
    }
  }, [messages, userId]);

  return (
    <Box sx={styles.container}>
      <GinoHeader
        ginoCredits={ginoCredits}
        isLoadingGinoCredits={isLoadingGinoCredits}
        errorGinoCredits={errorGinoCredits}
        openCreditLeftModal={openCreditLeftModal}
        handleOpenCreditLeftModal={handleOpenCreditLeftModal}
        handleCloseCreditLeftModal={handleCloseCreditLeftModal}
      />
      <Box sx={styles.mainContainer}>
        <Box ref={messagesContainerRef} sx={styles.messagesContainer}>
          {about && <GinoOpeningMessage askingAbout={about} />}
          {messages?.map((message) => (
            <GinoMessage key={message.id} message={message} />
          ))}
          {isSendingGinoMessages && <ThinkingMessage />}
        </Box>
        <GinoChat
          placeholder={t("askGino")}
          chatInputRef={chatInputRef}
          onSend={handleSendTextMessage}
          handleOnKeyDownWithinChatInput={handleOnKeyDownWithinChatInput}
          isLoading={isSendingGinoMessages || isLoadingMessages || isLoadingDirectRoom}
          ginoCredits={ginoCredits}
          isLoadingGinoCredits={isLoadingGinoCredits}
          handleOpenCreditLeftModal={handleOpenCreditLeftModal}
          isChatReady={Boolean(directRoom?.chatId)}
        />
      </Box>
    </Box>
  );
};

export default GinoChatPage;

const styles = {
  container: {
    width: "100%",
    height: "90vh",
    background: "var(--background-surface-default, #FFF)",
    padding: { xs: "0", md: "32px" },
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    overflow: "hidden",
  },
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "40px",
    padding: { xs: "0", md: "0 80px" },
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    flex: 1,
    overflow: "auto",
    paddingRight: "12px",
  },
};
